import React, { useCallback, useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from "recharts";
import { Download, HeartHandshake, Link2, Loader2, RefreshCw, ShieldOff, Siren, Sparkles, Timer, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const SERIES = [
  { key: "prayers", label: "Demandes", color: "#2D5A4C" },
  { key: "intercessions", label: "Intercessions (Je prie)", color: "#7BAA97" },
  { key: "encouragements", label: "Encouragements", color: "#E0A63C" },
  { key: "answered", label: "Exaucées", color: "#D97757" },
  { key: "chain_minutes", label: "Minutes de chaîne", color: "#5B7DB1" },
];
const COLS = [
  ["week_start", "Semaine du"], ["prayers", "Demandes"], ["urgent", "Urgentes"], ["intercessions", "Intercessions"],
  ["encouragements", "Encouragements"], ["reactions", "Réactions"], ["answered", "Exaucées"], ["chain_minutes", "Min. chaîne"],
  ["chain_people", "Pers. chaîne"], ["testimonies", "Témoignages"],
];

const Kpi = ({ label, value, hint, icon: Icon, color = "#2D5A4C", testId }) => (
  <div className="yalola-card p-4 flex items-start gap-3" data-testid={testId}>
    <span className="h-9 w-9 rounded-full grid place-items-center shrink-0" style={{ background: color + "1f" }}><Icon size={16} color={color} /></span>
    <div className="min-w-0">
      <div className="text-2xl font-bold tabular-nums" style={{ fontFamily: "Outfit" }}>{value ?? "—"}</div>
      <div className="text-xs font-semibold">{label}</div>
      {hint && <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{hint}</div>}
    </div>
  </div>
);

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr", { day: "2-digit", month: "2-digit" }); } catch { return iso; } };

export const PrayersPanel = ({ isSuper = false }) => {
  const [weeks, setWeeks] = useState(12);
  const [data, setData] = useState(null);
  const [urgent, setUrgent] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([api.get(`/admin/prayers/stats?weeks=${weeks}`), api.get("/admin/prayers/urgent")]);
      setData(s.data); setUrgent(u.data.items || []);
    } catch { toast.error("Impossible de charger les statistiques prières"); }
    setLoading(false);
  }, [weeks]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/admin/prayers/stats.csv?weeks=${weeks}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8;" }));
      const a = document.createElement("a"); a.href = url; a.download = `yalola-prieres-${weeks}sem.csv`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
  };

  const unflag = async (p) => {
    if (!window.confirm("Retirer le drapeau « urgent » de cette demande ? Elle reste sur le mur.")) return;
    try { await api.delete(`/admin/prayers/${p.id}/urgent`); toast.success("Drapeau retiré"); load(); } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const tot = data?.totals || {};
  const rows = data?.weeks || [];
  return (
    <div className="space-y-5" data-testid="admin-prayers-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}><HeartHandshake size={20} color="#2D5A4C" /> Prières — engagement</h2>
          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>Demandes, intercessions (« Je prie »), encouragements, prières exaucées et chaîne de prière, semaine par semaine (lundi → dimanche, UTC). Les comptes IA sont inclus dans les intercessions (voir « dont IA »).</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} className="h-9 rounded-full border px-3 text-sm bg-transparent" style={{ borderColor: "var(--yalola-border)" }} data-testid="admin-prayers-weeks">
            {[8, 12, 26, 52].map((w) => <option key={w} value={w}>{`${w} semaines`}</option>)}
          </select>
          <Button size="sm" variant="outline" className="rounded-full" onClick={load} disabled={loading} data-testid="admin-prayers-refresh"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></Button>
          <Button size="sm" className="rounded-full" style={{ background: "#2D5A4C", color: "#fff" }} onClick={exportCsv} data-testid="admin-prayers-export"><Download size={14} className="mr-1" /> Export CSV</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Demandes (total)" value={tot.prayers} hint={`${tot.open ?? 0} ouvertes · ${tot.answered ?? 0} exaucées`} icon={HeartHandshake} testId="admin-prayers-kpi-prayers" />
        <Kpi label="Taux d'exaucement" value={tot.answered_rate != null ? `${tot.answered_rate} %` : "—"} hint={`${tot.testimonies_from_prayers ?? 0} témoignage(s) issus d'une prière`} icon={Sparkles} color="#D97757" testId="admin-prayers-kpi-rate" />
        <Kpi label="Intercessions" value={tot.intercessions} hint={`dont IA : ${tot.bot_intercessions ?? 0} · ${tot.intercessors_30d ?? 0} intercesseurs humains (30 j)`} icon={Users} color="#7BAA97" testId="admin-prayers-kpi-intercessions" />
        <Kpi label="Minutes de chaîne" value={tot.chain_minutes} hint={`${tot.urgent_active ?? 0} urgente(s) active(s) · ${tot.encouragements ?? 0} encouragements`} icon={Timer} color="#5B7DB1" testId="admin-prayers-kpi-chain" />
      </div>

      <div className="yalola-card p-4" data-testid="admin-prayers-chart">
        <div className="h-72">
          {loading && !data ? <div className="h-full grid place-items-center"><Loader2 className="animate-spin" /></div> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--yalola-border)" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} interval={rows.length > 16 ? 3 : 0} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <ChartTooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} labelFormatter={(l, payload) => `Semaine du ${payload?.[0]?.payload?.week_start ? fmtDate(payload[0].payload.week_start) : l}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {SERIES.map((s) => <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[4, 4, 0, 0]} />)}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="yalola-card overflow-x-auto" data-testid="admin-prayers-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs" style={{ color: "var(--yalola-text-muted)" }}>{COLS.map(([k, l]) => <th key={k} className="px-3 py-2 font-semibold whitespace-nowrap">{l}</th>)}</tr></thead>
          <tbody>
            {[...rows].reverse().map((w) => (
              <tr key={w.week_start} className="border-t" style={{ borderColor: "var(--yalola-border)" }} data-testid={`admin-prayers-row-${w.week_start}`}>
                {COLS.map(([k]) => <td key={k} className="px-3 py-2 tabular-nums whitespace-nowrap">{k === "week_start" ? fmtDate(w[k]) : w[k]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="yalola-card p-4 space-y-3" data-testid="admin-prayers-urgent">
        <h3 className="font-semibold flex items-center gap-2"><Siren size={16} color="#D97757" /> Demandes urgentes actives ({urgent.length})</h3>
        {!urgent.length && <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>Aucune demande urgente en cours.</p>}
        {urgent.map((p) => (
          <div key={p.id} className="flex items-start gap-3 rounded-xl border p-3" style={{ borderColor: "var(--yalola-border)" }} data-testid={`admin-prayers-urgent-${p.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{p.is_anonymous ? "Anonyme" : p.author?.name} · {fmtDate(p.created_at)} → expire {fmtDate(p.urgent_until)}</div>
              <p className="text-sm line-clamp-2">{p.content}</p>
              <div className="text-[11px] mt-1 inline-flex items-center gap-2" style={{ color: "var(--yalola-text-muted)" }}><Link2 size={11} /> {p.chain?.minutes || 0} min · {p.chain?.people || 0} pers. · {p.pray_count} « Je prie »</div>
            </div>
            {isSuper && <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => unflag(p)} data-testid={`admin-prayers-unflag-${p.id}`}><ShieldOff size={14} className="mr-1" /> Retirer « urgent »</Button>}
          </div>
        ))}
      </div>
    </div>
  );
};
